import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ProjectContext } from "context/ProjectProvider";
import {
  EntityMilestoneAndDurationColumns,
  getMaxNumberOfMilestone
} from "entity-app/shared-components/entity-milestones-duration-columns";
import { AgGridReact } from "ag-grid-react";
import { getLoggedinUserId } from "services/auth";
import { useMutation } from "@apollo/client";
import { MUTATION_SAVE_COLUMNS } from "services/graphQL/mutations";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import { message } from "antd";
import {
  InfoFeatureType,
  TAuditChangeLog
} from "change-events/change-event-polling";
import {
  DESIGN_PACKAGE_LIST_VIEW,
  QUERY_GET_DESIGN_PACKAGE_COLUMN_CONFIG
} from "../queries";

export const useDesignPackageData = (props: {
  gridRef: React.RefObject<AgGridReact<any>>;
}) => {
  const { gridRef } = props;
  const { gqlClientForProject, eventLogs } = useContext(ProjectContext);
  const milestonesColumns = useMemo(
    () => EntityMilestoneAndDurationColumns(),
    []
  );
  const [maxNumberOfMilestone, setMaxNumberOfMilestone] = useState({
    numberOfMaxOffsets: 0,
    numberOfMaxMileStones: 0
  });

  const { data: designPackageList, refetch: refetchDesignPackageList } =
    useCIQQuery<{
      design_package_list_func: Array<any>;
    }>(DESIGN_PACKAGE_LIST_VIEW, {
      variables: {},
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const loggedinUserId = useMemo(() => getLoggedinUserId(), []);
  const { data: savedColumnsData } = useCIQQuery<{
    project_users: Array<{ design_package_column_config: any }>;
  }>(QUERY_GET_DESIGN_PACKAGE_COLUMN_CONFIG, {
    fetchPolicy: "no-cache",
    client: gqlClientForProject,
    skip: !gqlClientForProject || !loggedinUserId,
    variables: {
      where: { user_id: { _eq: loggedinUserId } }
    }
  });

  const previousEventLogs = useRef(eventLogs);

  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      /**
       * If info.feature_type == "DESIGN_PACKAGE" then on log page refresh everything
       * if any planning item of bid changed then milestone of design pkg might will change hence need to reload the list
       */
      const allImpactingFeatureTypes: any = [
        InfoFeatureType.designPackage,
        InfoFeatureType.bidPackage,
        InfoFeatureType.planningActivity,
        InfoFeatureType.planningMaterial,
        InfoFeatureType.planningSubmittal
      ];

      const conditionForBidPackage = (log: TAuditChangeLog) =>
        allImpactingFeatureTypes.includes(log?.info?.feature_type);

      if (eventLogs.some(conditionForBidPackage)) {
        refetchDesignPackageList();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetchDesignPackageList]);

  const calculateMaxNumberOfMilestone = useCallback(() => {
    const maxMileStone = getMaxNumberOfMilestone({
      api: gridRef.current?.api
    });
    if (!maxMileStone) return;

    setMaxNumberOfMilestone(maxMileStone);
    if (gridRef.current && maxMileStone)
      milestonesColumns.updateAllMileStoneInitialVisibility(
        gridRef.current?.api,
        maxMileStone
      );
  }, [gridRef, milestonesColumns]);

  const savedColumns = useMemo(() => {
    if (!savedColumnsData || !savedColumnsData?.project_users?.length)
      return null;

    const resArr = savedColumnsData?.project_users;
    const colConfig = resArr[resArr.length - 1];
    calculateMaxNumberOfMilestone();
    return colConfig;
  }, [calculateMaxNumberOfMilestone, savedColumnsData]);

  const [saveColumnsStateMutation] = useMutation<any>(MUTATION_SAVE_COLUMNS, {
    client: gqlClientForProject
  });

  const onSaveColumnState = async () => {
    console.log("onSaveColumnState ");
    if (!gridRef.current) return;
    const columnsStates = UtilColumnConfig.getColumnStateFromGrid({
      gridRef: {
        api: gridRef.current!.api
      }
    });

    try {
      const variables = {
        design_package_column_config: columnsStates
      };
      const updateResponse: any = await saveColumnsStateMutation({
        variables
      });
      if (updateResponse.errors) {
        message.error({ content: updateResponse.errors[0].message });
      } else {
        // message.success("Updated successfully");
      }
    } catch (ex) {
      console.log("An error occured");
    }
  };

  return {
    designPackageList,
    refetchDesignPackageList,
    milestonesColumns,
    maxNumberOfMilestone,
    calculateMaxNumberOfMilestone,
    savedColumns,
    onSaveColumnState
  };
};
